import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X, SlidersHorizontal } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
export const Products = () => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  return <div className="bg-white min-h-screen flex flex-col" data-id="element-1006">
      {/* Breadcrumbs */}
      <div className="bg-gray50 border-b border-gray200 py-3" data-id="element-1007">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-sm text-gray-500" data-id="element-1008">
          <Link to="/" className="hover:text-charcoal transition-colors" data-id="element-1009">
            Home
          </Link>
          <ChevronRight className="w-4 h-4" data-id="element-1010" />
          <span className="text-charcoal font-semibold" data-id="element-1011">Products</span>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-ink text-white py-12 relative overflow-hidden" data-id="element-1012">
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-1013">
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-id="element-1014">
            All Products & Categories
          </h1>
          <p className="text-gray-300 max-w-2xl text-lg" data-id="element-1015">
            Browse the full wholesale catalog of custom uniforms and team
            apparel built for dealers, resellers, and team stores. Select a
            category to start your design in 3D or apply for wholesale access to
            view pricing.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 flex-grow w-full flex flex-col md:flex-row gap-8" data-id="element-1016">
        {/* Mobile Filter Toggle */}
        <div className="md:hidden flex items-center justify-between mb-4" data-id="element-1017">
          <button onClick={() => setIsMobileFilterOpen(true)} className="flex items-center gap-2 text-charcoal font-semibold border border-gray200 px-4 py-2 rounded-sm" data-id="element-1018">
            <SlidersHorizontal className="w-4 h-4" data-id="element-1019" /> Categories
          </button>
          <span className="text-sm text-gray-500" data-id="element-1020">
            {categories.length} Categories
          </span>
        </div>

        {/* Sidebar (Desktop) */}
        <aside className="hidden md:block w-64 shrink-0" data-id="element-1021">
          <div className="sticky top-24" data-id="element-1022">
            <h3 className="font-heading font-bold text-lg mb-4 border-b border-gray200 pb-2" data-id="element-1023">
              Categories
            </h3>
            <ul className="flex flex-col gap-1" data-id="element-1024">
              {categories.map(cat => <li key={cat.slug} data-id="element-1025">
                  <Link to={`/category/${cat.slug}`} className="block w-full text-left px-3 py-2 rounded-sm text-sm font-medium text-gray-600 hover:bg-gray50 hover:text-charcoal transition-colors" data-id="element-1026">
                    {cat.name}
                  </Link>
                </li>)}
            </ul>

            <div className="mt-8 bg-gray50 p-4 rounded-lg border border-gray200" data-id="element-1027">
              <h4 className="font-bold text-sm mb-2" data-id="element-1028">Wholesale Pricing</h4>
              <p className="text-xs text-gray-600 mb-4" data-id="element-1029">
                Log in to your dealer account to view wholesale pricing and
                combined quantity discounts.
              </p>
              <Link to="/wholesale-apply" className="text-xs font-bold text-brandRed hover:text-brandRedDark flex items-center gap-1" data-id="element-1030">
                Apply for Access <ChevronRight className="w-3 h-3" data-id="element-1031" />
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-grow" data-id="element-1032">
          {/* Category Chips (Desktop) */}
          <div className="hidden md:flex flex-wrap gap-2 mb-8" data-id="element-1033">
            {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="px-4 py-1.5 rounded-full text-sm font-medium border bg-white border-gray200 text-gray-600 hover:border-charcoal hover:text-charcoal transition-colors" data-id="element-1034">
                {cat.name}
              </Link>)}
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1035">
            {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="group flex flex-col bg-white border border-gray200 rounded-lg overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all hover:-translate-y-1" data-id="element-1036">
                <div className="aspect-square bg-gray50 p-6 relative flex items-center justify-center" data-id="element-1037">
                  {cat.image && !cat.iconOnly ? <img src={cat.image} alt={cat.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" data-id="element-1038" /> : cat.icon ? <cat.icon className="w-20 h-20 text-charcoal group-hover:text-brandRed transition-colors" strokeWidth={1.5} data-id="element-1039" /> : <div className="w-20 h-20 rounded-full bg-brandRed/10" data-id="element-1040" />}
                </div>
                <div className="p-4 flex flex-col flex-grow" data-id="element-1041">
                  <h3 className="font-bold text-charcoal leading-tight mb-1 group-hover:text-brandRed transition-colors" data-id="element-1042">
                    {cat.name}
                  </h3>
                  {cat.shortLabel && <p className="text-sm text-gray-500 leading-tight" data-id="element-1043">
                      {cat.shortLabel}
                    </p>}
                  <div className="mt-auto pt-4 flex items-center justify-between" data-id="element-1044">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1045">
                      Wholesale
                    </span>
                    <span className="text-sm font-bold text-brandRed flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-1046">
                      View Category <ChevronRight className="w-4 h-4" data-id="element-1047" />
                    </span>
                  </div>
                </div>
              </Link>)}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <AnimatePresence data-id="element-1048">
        {isMobileFilterOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsMobileFilterOpen(false)} className="fixed inset-0 bg-ink/50 z-50 md:hidden" data-id="element-1049" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'tween',
          duration: 0.3
        }} className="fixed inset-y-0 left-0 w-4/5 max-w-sm bg-white z-50 shadow-2xl flex flex-col md:hidden" data-id="element-1050">
              <div className="p-4 border-b border-gray200 flex items-center justify-between bg-gray50" data-id="element-1051">
                <span className="font-heading font-bold text-lg" data-id="element-1052">
                  Categories
                </span>
                <button onClick={() => setIsMobileFilterOpen(false)} className="p-2 text-gray-500 hover:text-charcoal" data-id="element-1053">
                  <X className="w-5 h-5" data-id="element-1054" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-grow" data-id="element-1055">
                <ul className="flex flex-col gap-2" data-id="element-1056">
                  {categories.map(cat => <li key={cat.slug} data-id="element-1057">
                      <Link to={`/category/${cat.slug}`} onClick={() => setIsMobileFilterOpen(false)} className="block w-full text-left px-4 py-3 rounded-sm font-medium text-gray-600 hover:bg-gray100 hover:text-charcoal transition-colors" data-id="element-1058">
                        {cat.name}
                      </Link>
                    </li>)}
                </ul>
              </div>
              <div className="p-4 border-t border-gray200 bg-gray50" data-id="element-1059">
                <Link to="/wholesale-apply" className="block w-full bg-charcoal text-white text-center font-bold py-3 rounded-sm" data-id="element-1060">
                  Apply for Wholesale Access
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Bottom CTA Banner */}
      <div className="bg-gray100 border-t border-gray200 py-12 mt-auto" data-id="element-1061">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1062">
          <h2 className="text-2xl font-heading font-bold text-ink mb-6" data-id="element-1063">
            Ready to build your custom uniforms?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1064">
            <Link to="/products" className="w-full sm:w-auto bg-brandRed hover:bg-brandRedDark text-white font-bold py-3.5 px-8 rounded-sm transition-colors" data-id="element-1065">
              Start Your Design in 3D
            </Link>
            <Link to="/wholesale-apply" className="w-full sm:w-auto bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3 px-8 rounded-sm transition-colors" data-id="element-1066">
              Apply for Wholesale Access
            </Link>
          </div>
        </div>
      </div>
    </div>;
};